import * as React from 'react';
import { CustomRequestMap, DocAccessType } from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';
import { ICustomRequestDocList } from 'types';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: CustomRequestMap;
  warning?: string;
  userRole?: UserRole;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  onUploadClick: (customReqKey: string, docKey: string) => void;
  onOpenClick: (customReqKey: string, docKey: string) => void;
  onApproveClick: (customReqKey: string, docKey: string) => void;
  onRejectClick: (customReqKey: string, docKey: string) => void;
  onRescindRejectClick: (customReqKey: string, docKey: string) => void;
  onDeleteClick: (customReqKey: string, docKey: string) => void;
  onAcknowledgeClick: (customReqKey: string, docKey: string) => void;
}

const CustomRequestDocument = (props: IProps) => {
  const {
    infoOnly,
    userAccess,
    documents: customRequestDocuments = {},
    warning,
    userRole,
    canUpload,
    canOpen,
    canApprove,
    canReject,
    isDeleting,
    canAcknowledge,
    onUploadClick,
    onOpenClick,
    onApproveClick,
    onRejectClick,
    onRescindRejectClick,
    onDeleteClick,
    onAcknowledgeClick,
  } = props;

  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];
  const customRequestDocumentsKeys = Object.keys(customRequestDocuments).sort();

  customRequestDocumentsKeys.forEach(customReqKey => {
    const customDocs = customRequestDocuments[customReqKey];
    const visibleDocs = getVisible<CustomRequestMap, ICustomRequestDocList>({
      documentsHash: customDocs ?? {},
      userAccess,
      infoOnly,
      userRole,
    });
    if (hasDeleted(customDocs ?? {})) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc, index) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`custom-doc-${customReqKey}-${key}`}
              key={`custom-doc-${customReqKey}-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              description={item?.desc ?? item.reason}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${item.label} ${Object.keys(visibleDocs ?? {}).length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              canUpload={infoOnly ? false : canUpload}
              canOpen={infoOnly ? false : canOpen}
              canApprove={infoOnly ? false : canApprove}
              canReject={infoOnly ? false : canReject}
              isDeleting={infoOnly ? false : isDeleting}
              canAcknowledge={infoOnly ? canAcknowledge : false}
              onUploadClick={() => onUploadClick(customReqKey, key)}
              onOpenClick={() => onOpenClick(customReqKey, key)}
              onApproveClick={() => onApproveClick(customReqKey, key)}
              onRejectClick={() => onRejectClick(customReqKey, key)}
              onRescindRejectClick={() => onRescindRejectClick(customReqKey, key)}
              onDeleteClick={() => onDeleteClick(customReqKey, key)}
              onAcknowledgeClick={() => onAcknowledgeClick(customReqKey, key)}
              userRole={userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  });

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section id={`custom-request-section`} label={'Custom Request Document(s)'} warning={warning}>
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default CustomRequestDocument;
